import { useState } from 'react';
import { Link2, Unlink } from 'lucide-react';
import { formatEur, parseEurCents } from '@/lib/cashFlow';
import type { CashFlowEntry, CashFlowRequestOptions } from '@/lib/cashFlow';
import { useReconciliationActions, useReconciliationCandidates } from '@/hooks/useReconciliation';

export function ReconciliationPanel({
  entry,
  options,
  onOpenEntry,
}: {
  entry: CashFlowEntry;
  options?: CashFlowRequestOptions;
  onOpenEntry?: (id: string) => void;
}) {
  const r = entry.reconciliation;
  const [open, setOpen] = useState(false);
  const [candidateId, setCandidateId] = useState('');
  const [mode, setMode] = useState<'replacement' | 'partial'>('replacement');
  const [forecastAmount, setForecastAmount] = useState('');
  const [actualAmount, setActualAmount] = useState('');
  const { data: candidates, isLoading } = useReconciliationCandidates(open ? entry.id : undefined, options);
  const { confirm, unlink } = useReconciliationActions(options);

  const isForecast = entry.certainty === 'forecast';
  const forecast = r?.originalForecastCents ?? (isForecast ? entry.money.gross : 0);
  const ownAvailable = isForecast
    ? (r?.remainingForecastCents ?? entry.money.gross)
    : Math.max(0, entry.money.gross - (r?.linkedActualCents ?? 0));
  const selected = candidates?.find((c) => c.id === candidateId);
  const selectedAvailable = selected?.grossCents ?? 0;
  const partialForecastCents = parseEurCents(forecastAmount);
  const partialActualCents = parseEurCents(actualAmount);
  const forecastCents = mode === 'replacement'
    ? (isForecast ? ownAvailable : selectedAvailable)
    : (partialForecastCents ?? 0);
  const actualCents = mode === 'replacement'
    ? (isForecast ? selectedAvailable : ownAvailable)
    : (partialActualCents ?? 0);
  const valid = Boolean(
    selected &&
    forecastCents > 0 &&
    actualCents > 0 &&
    forecastCents <= (isForecast ? ownAvailable : selectedAvailable) &&
    actualCents <= (isForecast ? selectedAvailable : ownAvailable),
  );

  const submit = () => {
    // The submit button is disabled unless `valid`, and `valid` implies a
    // selected candidate — so `submit` only ever runs with a counterpart.
    if (!window.confirm('Kinnitada see prognoosi ja tegeliku kirje seos?')) return;
    confirm.mutate({
      forecastEntryId: isForecast ? entry.id : selected!.id,
      actualEntryId: isForecast ? selected!.id : entry.id,
      forecastCents,
      actualCents,
      mode,
    });
  };

  return (
    <section className="rounded-lg border border-[hsl(var(--pending))]/30 bg-[hsl(var(--pending))]/[.04] p-3">
      <div className="mb-3 flex items-center gap-2">
        <Link2 className="h-3.5 w-3.5 text-[hsl(var(--pending))]" />
        <h3 className="text-xs font-semibold">Prognoosi vastavus</h3>
        <span className="ml-auto rounded-full border border-border px-2 py-0.5 text-[10px]">
          {r?.isFullyReconciled ? 'Täielikult seotud' : r?.links.length ? 'Osaliselt seotud' : 'Sidumata'}
        </span>
      </div>

      <div className="grid grid-cols-2 gap-2 text-[11px]">
        <Metric label="Algne prognoos" value={formatEur(forecast)} />
        <Metric label="Seotud tegelik" value={formatEur(r?.linkedActualCents ?? 0)} />
        <Metric label="Prognoosi jääk" value={formatEur(r?.remainingForecastCents ?? (isForecast ? forecast : 0))} />
        <Metric label="Hälve" value={formatEur(r?.varianceCents ?? 0)} />
      </div>

      {r?.links.map((link) => (
        <div key={link.linkId} className="mt-3 border-t border-border/60 pt-2 text-[11px]">
          <div className="flex items-center gap-2">
            <button
              type="button"
              onClick={() => onOpenEntry?.(link.counterpart.id)}
              className="min-w-0 truncate text-left font-medium hover:underline"
            >
              {link.counterpart.party}
              {link.counterpart.docNumber ? ` · ${link.counterpart.docNumber}` : ''}
            </button>
            <span className="ml-auto whitespace-nowrap tabular-nums">
              P {formatEur(link.forecastCents)} · T {formatEur(link.actualCents)}
            </span>
            <button
              type="button"
              aria-label="Eemalda prognoosi seos"
              onClick={() => {
                if (window.confirm('Eemaldada see prognoosi ja tegeliku kirje seos?')) {
                  unlink.mutate(link.linkId);
                }
              }}
              disabled={unlink.isPending}
              className="text-muted-foreground hover:text-[hsl(var(--cost))]"
            >
              <Unlink className="h-3.5 w-3.5" />
            </button>
          </div>
          <div className="mt-1 text-[10px] text-muted-foreground">
            {link.mode === 'replacement' ? 'Asendus' : 'Osaline'} ·{' '}
            {link.matchSource === 'auto' ? 'automaatne' : 'käsitsi'}
            {link.matchReason ? ` · ${link.matchReason}` : ''}
          </div>
        </div>
      ))}

      <button
        type="button"
        onClick={() => setOpen((value) => !value)}
        disabled={ownAvailable <= 0}
        className="mt-3 rounded-md border border-border px-2.5 py-1.5 text-[11px] hover:bg-accent disabled:opacity-40"
      >
        {open ? 'Sulge vastete otsing' : 'Seo käsitsi'}
      </button>

      {open && (
        <div className="mt-3 space-y-2 border-t border-border/60 pt-3">
          <div className="text-[10px] text-muted-foreground">
            Vali vastaspool. „Asendus” seob mõlema poole kogu vaba mahu ja säilitab summade erinevuse hälbena.
          </div>
          {isLoading ? (
            <div className="h-8 animate-pulse rounded bg-muted" />
          ) : (
            <select
              aria-label="Vastaspool"
              value={candidateId}
              onChange={(event) => setCandidateId(event.target.value)}
              className="w-full rounded border border-border bg-background px-2 py-1.5 text-xs"
            >
              <option value="">Vali kirje…</option>
              {candidates?.map((candidate) => (
                <option key={candidate.id} value={candidate.id}>
                  {candidate.party} · vaba {formatEur(candidate.grossCents)}
                </option>
              ))}
            </select>
          )}
          <div className="flex gap-2">
            <select
              aria-label="Sidumise viis"
              value={mode}
              onChange={(event) => setMode(event.target.value as typeof mode)}
              className="rounded border border-border bg-background px-2 py-1.5 text-xs"
            >
              <option value="replacement">Asendus</option>
              <option value="partial">Osaline</option>
            </select>
            {mode === 'partial' && (
              <div className="grid min-w-0 flex-1 grid-cols-2 gap-2">
                <input
                  aria-label="Seotav prognoosisumma eurodes"
                  inputMode="decimal"
                  value={forecastAmount}
                  onChange={(event) => setForecastAmount(event.target.value)}
                  placeholder="Prognoos €"
                  className="min-w-0 rounded border border-border bg-background px-2 py-1.5 text-xs"
                />
                <input
                  aria-label="Seotav tegelik summa eurodes"
                  inputMode="decimal"
                  value={actualAmount}
                  onChange={(event) => setActualAmount(event.target.value)}
                  placeholder="Tegelik €"
                  className="min-w-0 rounded border border-border bg-background px-2 py-1.5 text-xs"
                />
              </div>
            )}
          </div>
          {selected && (
            <div className="text-[10px] text-muted-foreground">
              Prognoos {formatEur(forecastCents)} · tegelik {formatEur(actualCents)} · hälve{' '}
              {formatEur(forecastCents - actualCents)}
            </div>
          )}
          {(confirm.isError || unlink.isError) && (
            <div role="alert" className="text-[11px] text-[hsl(var(--cost))]">
              Seose muutmine ebaõnnestus. Raha ei muudetud.
            </div>
          )}
          <button
            type="button"
            disabled={!valid || confirm.isPending}
            onClick={submit}
            className="w-full rounded-md bg-[hsl(var(--primary))] px-3 py-1.5 text-xs text-[hsl(var(--primary-foreground))] disabled:opacity-40"
          >
            {confirm.isPending ? 'Seon…' : 'Kinnita käsitsi seos'}
          </button>
        </div>
      )}
    </section>
  );
}

function Metric({ label, value }: { label: string; value: string }) {
  return (
    <div>
      <div className="text-muted-foreground">{label}</div>
      <div className="font-medium tabular-nums">{value}</div>
    </div>
  );
}